import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import {
  AngularFirestore,
  AngularFirestoreCollection,
} from '@angular/fire/compat/firestore';
import { deleteField, serverTimestamp } from '@angular/fire/firestore';
import {
  Functions,
  getFunctions,
  httpsCallable,
} from '@angular/fire/functions';
import { NGXLogger } from 'ngx-logger';
import { sort } from 'radash';
import { first, from, map, Observable, take } from 'rxjs';
import { environment } from '../../environments/environment';
import { AppException, AppExceptionModel } from '../models/app-exception.model';
import { SchoolMember, SchoolMemberType } from '../models/member.school.model';
import { MembershipStatus } from '../models/membership.status';
import { UtilsService } from './utils.service';

@Injectable({
  providedIn: 'root',
})
export class SchoolMemberService {
  http = inject(HttpClient);
  private functions = inject(Functions);
  private logger: NGXLogger = inject(NGXLogger);
  utilsService = inject(UtilsService);
  constructor(private afs: AngularFirestore) {}

  getMembers({
    source,
    groupId,
    getMemberType,
    status,
    gradeId,
    academicYear,
    lastRecord,
    limit,
  }: {
    source: string;
    groupId: string;
    getMemberType?: SchoolMemberType[];
    status?: MembershipStatus[];
    gradeId?: string;
    academicYear?: number;
    lastRecord?: SchoolMember;
    limit?: number;
  }): Observable<SchoolMember[]> {
    this.logger.debug(
      `SchoolMemberService :: getMembers :: Source: ${source} Fetching ${getMemberType}:: GroupId: ${groupId}, Status: ${status}, GradeId: ${gradeId}, academicYear: ${academicYear}, LastRecord: ${lastRecord?.modified.toDate()}, ${
        lastRecord?.id
      }`,
    );
    try {
      let query: AngularFirestoreCollection<SchoolMember> =
        this.afs.collection<SchoolMember>('memberships', (ref) => {
          var reference = ref.where('groupId', '==', groupId);

          if (status) reference = reference.where('status', 'in', status);

          if (getMemberType) {
            reference = reference.where(
              'memberType',
              'array-contains-any',
              getMemberType,
            );
          }

          if (gradeId) reference = reference.where('gradeId', '==', gradeId);

          if (academicYear)
            reference = reference.where('gradeYear', '==', academicYear);

          reference = reference.orderBy('modified', 'desc');

          reference = reference.startAt(
            lastRecord ? lastRecord?.modified.toDate() : new Date(),
          );

          reference = reference.limit(limit ? limit + 1 : 11);

          return reference;
        });

      return from(query.get()).pipe(
        first(),
        map((memberships) => {
          if (memberships.empty) return [];
          const members: SchoolMember[] = memberships.docs
            .map((doc) => {
              try {
                const member = doc.data() as SchoolMember;
                member.id = doc.id;
                return member;
              } catch (error) {
                return null;
              }
            })
            .filter((member) => member !== null);
          return members;
        }),
      );
    } catch (error) {
      this.logger.error(
        `SchoolMemberService :: getMembers: Error: ${JSON.stringify(error)}`,
      );
      throw new Error(
        JSON.stringify({
          code: 'ERR_GET_MEMBERS',
          title: 'Unable to fetch members',
          message:
            'An error occurred while fetching members. Please try again later',
        }),
      );
    }
  }

  async getParentsMembers({
    source,
    groupId,
    gradeId,
    academicYear,
    lastRecord,
    limit,
  }: {
    source: string;
    groupId: string;
    gradeId?: string;
    academicYear?: number;
    lastRecord?: SchoolMember;
    limit?: number;
  }): Promise<SchoolMember[]> {
    // this.logger.log(
    //   `SchoolMemberService :: getParentsMembers :: Source: ${source}, GroupId: ${groupId}, Status: ${status}, GradeId: ${gradeId}, academicYear: ${academicYear}, LastRecord: ${lastRecord?.modified.toDate()}, ${
    //     lastRecord?.id
    //   }`
    // );
    let query: AngularFirestoreCollection<SchoolMember> =
      this.afs.collection<SchoolMember>('memberships', (ref) => {
        var reference = ref.where('groupId', '==', groupId);
        reference = reference.where('status', 'in', [MembershipStatus.active]);
        reference = reference.where('memberType', 'array-contains-any', [
          SchoolMemberType.parent,
        ]);
        if (gradeId) reference = reference.where('gradeId', '==', gradeId);
        if (academicYear)
          reference = reference.where('gradeYear', '==', academicYear);

        reference = reference.orderBy('modified', 'desc');
        // .orderBy('name', 'desc');

        if (lastRecord) reference = reference.startAt(lastRecord.modified);
        // if (lastRecord)
        //   reference = reference.startAt(
        //     Timestamp.fromDate(lastRecord.modified.toDate())
        //   );

        reference = reference.limit(limit ?? 10);
        return reference;
      });
    try {
      return new Promise<SchoolMember[]>((resolve, reject) => {
        query
          .get()
          .pipe(first())
          .subscribe({
            next: (memberships) => {
              if (memberships.empty) {
                resolve([]);
                return;
              }
              const members: SchoolMember[] = memberships.docs
                .map((doc) => {
                  try {
                    const member = doc.data() as SchoolMember;
                    member.id = doc.id;
                    return member;
                  } catch (error) {
                    return null;
                  }
                })
                .filter((member) => member !== null);
              resolve(members);
            },
            error: (error) => {
              reject(error);
            },
          });
      });
    } catch (error) {
      this.logger.log(
        `SchoolMemberService :: getParentsMembers: Error: ${JSON.stringify(
          error,
        )}`,
      );
      throw new Error(
        JSON.stringify({
          code: 'ERR_GET_MEMBERS',
          title: 'Unable to fetch members',
          message:
            'An error occurred while fetching members. Please try again later',
        }),
      );
    }
  }

  getMembersByParentId(
    groupId: string,
    parentId: string,
  ): Observable<SchoolMember[]> {
    // this.logger.log(
    //   `SchoolMemberService :: getMembersByParentId :: Fetching GroupId: ${groupId}, ParentId: ${parentId}`
    // );
    let query: AngularFirestoreCollection<SchoolMember> =
      this.afs.collection<SchoolMember>('memberships', (ref) => {
        return ref
          .where('groupId', '==', groupId)
          .where('parentId', '==', parentId)
          .where('memberType', 'array-contains', SchoolMemberType.student)
          .orderBy('modified', 'desc');
      });
    return from(query.get()).pipe(
      take(1),
      map((memberships) => {
        if (memberships.empty) return [];
        const members: SchoolMember[] = memberships.docs
          .map((doc) => {
            try {
              const member = doc.data() as SchoolMember;
              member.id = doc.id;
              // this.logger.log(`Member: ${JSON.stringify(member)}`);
              return member;
            } catch (error) {
              return null;
            }
          })
          .filter((member) => member !== null); // Filter out null members
        return members;
      }),
    );
  }

  async getMember(groupId: string, memberId: string) {
    try {
      const snapshot = this.afs
        .collection('memberships', (ref) =>
          ref
            .where('groupId', '==', groupId)
            .where('memberId', '==', memberId)
            .limit(1),
        )
        .get();
      return snapshot.pipe(
        take(1),
        map((doc) => {
          if (doc.empty) return null;
          const member = doc.docs[0].data() as SchoolMember;
          member.id = doc.docs[0].id;
          return member;
        }),
      );
    } catch (error) {
      return Promise.reject(error);
    }
  }

  async searchMembers(
    searchTerm: string,
    groupId: string,
    filterFields?: Object,
  ): Promise<any> {
    this.logger.log(
      `MemberSchoolService :: searchMembers: Search '${searchTerm}' in group '${groupId}'`,
    );
    try {
      const callable = httpsCallable(this.functions, 'searchMembersByFilters'); //searchMembersByFilters
      const result = await callable({
        searchTerm: searchTerm,
        groupId: groupId,
        filterFields: filterFields,
      });
      const data: any[] = (result.data as any[]) ?? [];
      const sortList = sort(data, (f) => f.score);
      return sortList.map((item) => item.membership);
    } catch (error) {
      // this.logger.log(`MemberSchoolService :: searchMembers: Error: ${error}`);
      return Promise.reject('Unable to search members');
    }
  }

  async searchFirestoreMembers(
    searchTerm: string,
    groupId: string,
    filterFields?: Object,
  ): Promise<any> {
    this.logger.log(
      `MemberSchoolService :: searchFirestoreMembers: Search '${searchTerm}' in group '${groupId}'`,
    );
    try {
      const result = this.afs
        .collection('memberships', (ref) => {
          let reference = ref
            .where('groupId', '==', groupId)
            .where('name', '>=', searchTerm);

          if (filterFields) {
            Object.keys(filterFields).forEach((field) => {
              reference = reference.where(
                field,
                '==',
                (filterFields as any)[field],
              );
            });
          }
          return reference;
        })
        .get();
      this.logger.log(`Response: ${JSON.stringify(result)}`);
      return result.pipe(
        take(1),
        map((querySnapshot) => {
          return querySnapshot.docs.map((doc) => {
            const data = doc.data() as SchoolMember;
            return { ...data, id: doc.id } as SchoolMember;
          });
        }),
      );
    } catch (error) {
      this.logger.log(`MemberSchoolService :: searchMembers: Error: ${error}`);
      return Promise.reject('Unable to search members');
    }
  }

  create(member: SchoolMember): Promise<void> {
    try {
      return this.afs
        .collection('memberships')
        .doc(`${member.groupId}_${member.memberId}`)
        .set(member);
    } catch (error) {
      return Promise.reject(error);
    }
  }

  update(id: string, member: Partial<SchoolMember>): Promise<void> {
    this.logger.log(
      `MemberSchoolService :: update: Member: ${id}, Data: ${JSON.stringify(
        member,
        null,
        2,
      )}`,
    );
    let modifiedMember = member;
    delete modifiedMember.created;
    return this.afs.collection('memberships').doc(id).update(modifiedMember);
  }

  async delete(id: string): Promise<void> {
    // this.logger.log(`MemberSchoolService :: delete: Member ID: ${id}`);
    return this.afs.collection('memberships').doc(id).delete();
  }

  async createStudentUser(request: any): Promise<any> {
    const functions = getFunctions();
    const addMessage = httpsCallable(functions, 'createUnregisteredStudent');
    const result = await addMessage(request);
    return result.data;
  }

  async getMemberStats(groupId: string): Promise<any> {
    try {
      const functions = getFunctions();
      const callable = httpsCallable(functions, 'memberCount');
      const result = await callable({
        groupId,
      });
      // this.logger.log(`Response: ${JSON.stringify(result)}`);
      return result.data;
    } catch (error) {
      // this.logger.log(`MemberSchoolService :: getMemberStats: Error: ${error}`);
      return Promise.reject('Unable to fetch Member Statistics');
    }
  }

  /**
   * Links a parent to a student. The first parent stays the primary `parentId`
   * (what deployed mobile apps read); every parent is listed in `parentIds`.
   */
  async linkParent(student: SchoolMember, parent: SchoolMember): Promise<any> {
    this.logger.debug(
      `MemberSchoolService :: linkParent: Student: ${student.id}, Parent: ${parent.memberId}`,
    );
    try {
      await this.afs.firestore.runTransaction(async (transaction) => {
        const studentRef = this.afs.collection('memberships').doc(student.id).ref;
        const parentRef = this.afs.collection('memberships').doc(parent.id).ref;
        const current = ((await transaction.get(studentRef)).data() ?? {}) as SchoolMember;

        const parentIds = Array.from(
          new Set([
            ...(current.parentIds ?? []),
            ...(current.parentId ? [current.parentId] : []),
            parent.memberId,
          ]),
        );
        const keepPrimary = !!current.parentId && current.parentId !== parent.memberId;
        const parentName = keepPrimary ? current.parentName : student.parentName;
        transaction.update(studentRef, {
          ...student,
          parentId: keepPrimary ? current.parentId : parent.memberId,
          parentName: parentName ?? deleteField(),
          parentIds,
        });
        transaction.update(parentRef, parent);
      });
      this.logger.debug(`MemberSchoolService :: linkParent: linked`);
    } catch (error) {
      this.logger.error(`MemberSchoolService :: linkParent: Error: ${error}`);
      return Promise.reject('Unable to link parent');
    }
  }

  /** Unlinks a parent; the next linked parent, if any, becomes the primary */
  async delinkParent(
    student: SchoolMember,
    parent: SchoolMember,
  ): Promise<any> {
    this.logger.debug(
      `MemberSchoolService :: delinkParent: Student: ${student.id}, Parent: ${parent.memberId}`,
    );
    try {
      await this.afs.firestore.runTransaction(async (transaction) => {
        const studentRef = this.afs.collection('memberships').doc(student.id).ref;
        const parentRef = this.afs.collection('memberships').doc(parent.id).ref;
        const current = ((await transaction.get(studentRef)).data() ?? {}) as SchoolMember;

        const parentIds = Array.from(
          new Set([
            ...(current.parentIds ?? []),
            ...(current.parentId ? [current.parentId] : []),
          ]),
        ).filter((id) => id !== parent.memberId);
        const primaryRemoved =
          !current.parentId || current.parentId === parent.memberId;

        transaction.update(studentRef, {
          ...student,
          parentIds: parentIds.length > 0 ? parentIds : deleteField(),
          ...(primaryRemoved
            ? parentIds.length > 0
              ? { parentId: parentIds[0], parentName: deleteField() }
              : { parentId: deleteField(), parentName: deleteField() }
            : {
                parentId: current.parentId,
                parentName: current.parentName ?? deleteField(),
              }),
        });
        transaction.update(parentRef, parent);
      });
      this.logger.debug(`MemberSchoolService :: delinkParent: unlinked`);
    } catch (error) {
      this.logger.error(`MemberSchoolService :: delinkParent: Error: ${error}`);
      return Promise.reject('Unable to delink child');
    }
  }
}
